import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { IntroductionSection } from '../../types/documentation';
import LinkedText from '../common/LinkedText';
import { parseTextWithLinks } from '../../utils/linkParser';

interface IntroductionSectionComponentProps {
  introduction: IntroductionSection;
}

const IntroductionSectionComponent: React.FC<IntroductionSectionComponentProps> = ({
  introduction
}) => {
  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          alignItems: 'center',
          backgroundColor: '#fff',
          borderRadius: 3,
          p: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e3f2fd'
        }}
      >
        {/* Left Side - Content */}
        <Box sx={{ flex: 1, order: { xs: 2, md: 1 }, textAlign: 'left' }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: '#1976d2',
              mb: 2,
              textAlign: 'left'
            }}
          >
            {introduction.title}
          </Typography>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: '#666',
              mb: 3,
              textAlign: 'left'
            }}
          >
            <LinkedText text={introduction.subtitle} />
          </Typography>
          {introduction.description.map((paragraph, index) => (
            <Typography
              key={index}
              variant="body1"
              paragraph
              sx={{
                fontSize: '1.1rem',
                lineHeight: 1.7,
                color: '#555',
                mb: 2,
                textAlign: 'left'
              }}
            >
              <LinkedText text={paragraph} />
            </Typography>
          ))}
        </Box>

        {/* Right Side - Image */}
        <Box
          sx={{
            flex: 1,
            order: { xs: 1, md: 2 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '400px',
              height: '300px',
              // backgroundColor: '#f5f7fa',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // border: '2px dashed #c4d5e8',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <img
              src={introduction.imagePath}
              alt={introduction.imageAlt}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
              onError={(e) => {
                // Fallback content if image doesn't load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = `
                    <div style="text-align: center; color: #666;">
                      <div style="font-size: 48px; margin-bottom: 16px;">📱</div>
                      <div style="font-size: 18px; font-weight: 600;">Communication Platform</div>
                      <div style="font-size: 14px; margin-top: 8px;">Voice • Chat • Collaboration</div>
                    </div>
                  `;
                }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Features Section */}
      {introduction.features && introduction.features.length > 0 && (
        <Box sx={{
          mt: 4,
          backgroundColor: '#fff',
          borderRadius: 3,
          p: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e3f2fd'
        }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>
            Features
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {introduction.features.map((feature, index) => {
              const colors = [
                { bg: '#e3f2fd', color: '#1976d2' },
                { bg: '#e8f5e8', color: '#2e7d2e' },
                { bg: '#fff3e0', color: '#f57c00' },
                { bg: '#fce4ec', color: '#c2185b' }
              ];
              const colorSet = colors[index % colors.length];
              
              // Parse the feature text to check if it contains a link
              const segments = parseTextWithLinks(feature);
              const hasLink = segments.some(seg => seg.type === 'link');
              const linkSegment = segments.find(seg => seg.type === 'link');
              
              // If feature contains a link, make the chip clickable
              if (hasLink && linkSegment && linkSegment.url) {
                const isExternal = linkSegment.external;
                return (
                  <Chip
                    key={index}
                    label={linkSegment.content}
                    component="a"
                    href={linkSegment.url}
                    target={isExternal ? '_blank' : '_self'}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    clickable
                    sx={{
                      backgroundColor: colorSet.bg,
                      color: colorSet.color,
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': {
                        backgroundColor: colorSet.bg,
                        filter: 'brightness(0.95)',
                      }
                    }}
                  />
                );
              }
              
              // Regular chip without link
              return (
                <Chip
                  key={index}
                  label={feature}
                  sx={{
                    backgroundColor: colorSet.bg,
                    color: colorSet.color,
                    fontWeight: 600
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default IntroductionSectionComponent;
